/**
 * Fotoğraf büyütme (lightbox) — zoom in/out destekli.
 *
 * Mevcut Dialog bileşeni yeniden kullanılır; yeni bir modal altyapısı
 * getirilmez. Kaynak, mevcut medya sistemidir (`/api/media/{id}`).
 *
 * Kullanım:
 *   const [zoom, setZoom] = useState(null);
 *   <img onClick={() => setZoom(url)} />
 *   <ImageLightbox src={zoom} alt="..." onClose={() => setZoom(null)} />
 *
 * MOBİL: çift dokunma yakınlaştırır/sıfırlar, tek parmakla sürükleyerek
 * kaydırma yapılır (yakınlaşmışken). Masaüstünde Ctrl/⌘+tekerlek de çalışır.
 * Kapatma: ✕ düğmesi, arka plana tıklama veya ESC (Dialog'un kendi davranışı).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const STEP = 0.5;

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

export default function ImageLightbox({ src, alt = "Fotoğraf", onClose }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const lastTap = useRef(0);

  const open = !!src;

  // Her açılışta sıfırla — önceki fotoğrafın zoom'u taşınmasın.
  useEffect(() => {
    if (open) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, src]);

  const applyZoom = useCallback((next) => {
    setZoom((prev) => {
      const z = clampZoom(typeof next === "function" ? next(prev) : next);
      // Tam ekrana dönünce kaydırma da sıfırlanmalı, yoksa görsel kayık kalır.
      if (z === MIN_ZOOM) setOffset({ x: 0, y: 0 });
      return z;
    });
  }, []);

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return; // sayfa kaydırmayı ele geçirme
    e.preventDefault();
    applyZoom((z) => z + (e.deltaY < 0 ? STEP : -STEP));
  };

  const startDrag = (e) => {
    if (zoom <= MIN_ZOOM) return;
    const point = e.touches?.[0] || e;
    drag.current = { x: point.clientX - offset.x, y: point.clientY - offset.y };
  };

  const moveDrag = (e) => {
    if (!drag.current) return;
    const point = e.touches?.[0] || e;
    setOffset({
      x: point.clientX - drag.current.x,
      y: point.clientY - drag.current.y,
    });
  };

  const endDrag = () => {
    drag.current = null;
  };

  // Mobilde çift dokunma: yakınlaştır / sıfırla
  const onTouchEnd = (e) => {
    endDrag(e);
    const now = Date.now();
    if (now - lastTap.current < 300) {
      applyZoom((z) => (z > MIN_ZOOM ? MIN_ZOOM : 2));
    }
    lastTap.current = now;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="rounded-sm max-w-[95vw] sm:max-w-3xl p-0 overflow-hidden bg-background"
        data-testid="image-lightbox"
      >
        {/* Radix erişilebilirlik için başlık ister; görsel olarak gizli. */}
        <DialogTitle className="sr-only">{alt}</DialogTitle>

        <div
          className="relative w-full overflow-hidden bg-black/90 flex items-center justify-center"
          style={{ height: "min(72vh, 640px)" }}
          onWheel={onWheel}
          onMouseDown={startDrag}
          onMouseMove={moveDrag}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={startDrag}
          onTouchMove={moveDrag}
          onTouchEnd={onTouchEnd}
        >
          {src && (
            <img
              src={src}
              alt={alt}
              draggable={false}
              className="max-h-full max-w-full select-none"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transition: drag.current ? "none" : "transform 120ms ease-out",
                cursor: zoom > MIN_ZOOM ? "grab" : "default",
                touchAction: "none",
              }}
              data-testid="lightbox-image"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border">
          <span className="text-xs font-mono text-muted-foreground" data-testid="lightbox-zoom">
            %{Math.round(zoom * 100)}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => applyZoom((z) => z - STEP)}
              aria-label="Uzaklaştır"
              data-testid="lightbox-zoom-out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => applyZoom((z) => z + STEP)}
              aria-label="Yakınlaştır"
              data-testid="lightbox-zoom-in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              disabled={zoom === MIN_ZOOM && offset.x === 0 && offset.y === 0}
              onClick={() => applyZoom(MIN_ZOOM)}
              aria-label="Sıfırla"
              data-testid="lightbox-zoom-reset"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
